// -----------------------------------------------------------------------------
// Libraries
// -----------------------------------------------------------------------------
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import MuxPlayer from "@mux/mux-player-react";
import ReactPlayer from "react-player/lazy";
import mux from "mux-embed";
import axios from "axios";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Col from "antd/lib/col";
import Divider from "antd/lib/divider";
import Row from "antd/lib/row";
import Slider from "antd/lib/slider";

// -------------------------------------------------------------------------------
// Store
// -------------------------------------------------------------------------------
import { useGetDomainGymQuery, useLazyGetWorkoutNextMixQuery, useSendSongPlayStatsMutation } from "store/api/api";

// -------------------------------------------------------------------------------
// Components
// -------------------------------------------------------------------------------
import { userAtom } from "../../../globalAtoms";
import CoachSVG from "../../../images/coachIcon.svg";
import highVolumeSVG from "../../../images/highVolume.svg";
import homeIcon from "../../../images/homeIcon.svg";
import lowVolumeSVG from "../../../images/lowVolume.svg";
import MusicSVG from "../../../images/music.svg";
import PauseSVG from "../../../images/pause.svg";
import PlaySVG from "../../../images/play.svg";
import skipNextIcon from "../../../images/skip-next-white.svg";
import ajaxRoutes from "../../../utils/ajaxRoutes";
import Ajax from "../../../utils/api";
import MemberSubscriptionModal from "components/Modals/MemberSubscriptionModal";
import { LoadingMask } from "components/Common/LoadingMask";
import Layout from "./Layout";

// -------------------------------------------------------------------------------
// Utils, Styles and Assets
// -------------------------------------------------------------------------------
import { decodeString } from "utils";
import { USER_TYPES, isUserCreator, isUserAdmin } from "constants/ui";
import "./styles.scss";
import { workoutTypes } from "helpers/constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------
function FullScreenWorkoutVideoMusicPlayer() {
	const navigate = useNavigate();
	const [state, setState] = useState({
		playing: true,
		musicVolume: 0.3,
		coachVolume: 0.8,
		played: 0,
		videoUrl: null,
		audioUrl: null,
		showing: true,
		mixTracksSeconds: 0,
		totalSeconds: [],
	});
	const [paymentRequired, setPaymentRequired] = useState(false);
	const [live, setLive] = useState(false);
	const [playbackToken, setPlaybackToken] = useState(null);
	const [thumbnailToken, setThumbnailToken] = useState(null);
	const [playingMusic, setPlayingMusic] = useState(true);
	const [currentTrack, setCurrentTrack] = useState("");
	const [currentArtist, setCurrentArtist] = useState("");
	const [streamPlaybackId, setStreamPlaybackId] = useState(null);
	const [nextMix, setNextMix] = useState(null);
	const [userType, setUserType] = useState(USER_TYPES.CONSUMER);
	const [currentTrackId, setCurrentTrackId] = useState(null);
	const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
	const [skipTrack, setSkipTrack] = useState(false);
	const user = useRecoilValue(userAtom);
	const musicRef = useRef(null);
	const { workoutID } = useParams();
	const { gym } = useParams();
	const location = useLocation();
	const { data: domainGymData, isLoading: domainGymDataLoading } = useGetDomainGymQuery(gym);
	const [getWorkoutNextMixTrigger, getWorkoutNextMixResponse] = useLazyGetWorkoutNextMixQuery();
	const [sendSongPlayStatsTrigger] = useSendSongPlayStatsMutation();
	const internalPlayerRef = useRef(null);
	const videoRef = useRef(null);
	const livePlayerRef = useRef(null);
	const paneControlRef = useRef();

	const MIN_REPORT_PLAY_LENGTH = 30; // seconds
	const TRACK_END_REASON = {
		NATURAL_COMPLETION: 1,
		BANNED: 2,
		USER_SKIPPED: 3,
		ERROR: 4,
		DEFAULT_OTHER: 5,
	};

	const isScheduledPreRecorded = state.workout?.previous_type === workoutTypes.SCHEDULED_PRE_RECORDED;
	const assetStartTime = state.workout?.asset_start_time; // in seconds
	const assetEndTime = state.workout?.asset_end_time; // in seconds
	const scheduledStartTime = dayjs.tz(state.workout?.start_time, state.workout?.time_zone);
	// Calculate the time to end the workout
	const scheduledEndTime = scheduledStartTime.add(assetEndTime, "seconds");
	const currentTime = dayjs.tz();
	const differenceInSeconds = currentTime.diff(scheduledStartTime, "seconds");
	const timeToBegin = Math.min(differenceInSeconds + (assetStartTime || 0), state.workout?.duration) ?? 0;
	const isBeforeEndTime = currentTime.isBefore(scheduledEndTime);

	const playedInPercents = useMemo(() => {
		return (state.played / state.totalSeconds) * 100;
	}, [state.played, state.totalSeconds]);

	useEffect(() => {
		async function startWorkout() {
			const body = {
				workout_id: workoutID,
				user_id: user?.id,
				gym_id: domainGymData?.id,
			};
			try {
				const jwt = localStorage.getItem("creator_jwt_token");
				const headers = {
					Authorization: jwt,
				};
				const response = await axios.post(ajaxRoutes.START_WORKOUT, body, { headers });
				if (response?.data?.workout) {
					setLive(location.pathname.includes("live"));
					const { workout } = response.data;
					let videoUrl = workout?.video_url;
					setStreamPlaybackId(workout?.playback_id);
					const lengthSplit = workout?.mix?.length?.split(":");
					const totalSeconds =
						lengthSplit && lengthSplit.length >= 3
							? +lengthSplit[0] * 60 * 60 + +lengthSplit[1] * 60 + +lengthSplit[2]
							: 0;
					const mixTracksSeconds = workout?.mix?.tracks?.map((track) => {
						const a = track.time.split(":");
						const currentValue = +a[0] * 60 * 60 + +a[1] * 60 + +a[2];
						return currentValue;
					});

					if (!videoUrl) {
						const { token, thumbnail_token, url } = await Ajax.call(ajaxRoutes.GET_MUX_VIDEO_URL(workout?.playback_id));
						videoUrl = url;
						setPlaybackToken(token);
						setThumbnailToken(thumbnail_token);
					}

					setState((prevState) => ({
						...prevState,
						videoUrl: videoUrl || null,
						workout: workout,
						audioUrl: workout?.mix?.url,
						mix: workout?.mix,
						mixTracksSeconds,
						totalSeconds,
					}));
				} else {
					navigate(`/${gym}/dashboard/on-demand`);
				}
			} catch (e) {
				console.debug("ERROR: ", e);
				if (e?.response?.status === 402) {
					setPaymentRequired(true);
				} else {
					navigate(`/${gym}/dashboard/on-demand`);
				}
			}
		}

		if (!domainGymDataLoading) {
			startWorkout();
		}
	}, [domainGymDataLoading]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const associationsGymIds = user?.userAssociations?.map((a) => a.gym_id);
		const association = user?.userAssociations?.find((a) => a.gym_id === domainGymData?.id);

		if (associationsGymIds?.includes(domainGymData?.id) || isUserAdmin(user?.user_type)) {
			setUserType(association?.type);
		}
	}, [user, domainGymData]);

	useEffect(() => {
		if (state.workout && state.mixTracksSeconds) {
			const tracks = state.workout.mix.tracks;
			let currentIndex = 0;

			for (let i = 0; i < state.mixTracksSeconds.length; i++) {
				if (state.played >= state.mixTracksSeconds[state.mixTracksSeconds.length - 1]) {
					currentIndex = state.mixTracksSeconds.length - 1;
					break;
				}

				if (state.mixTracksSeconds[i] > state.played && i > 0) {
					currentIndex = i - 1;
					break;
				}
			}

			if (currentIndex !== currentTrackIndex) {
				if (currentTrackId && !skipTrack) {
					const playLength = getPlayLength(currentTrackIndex, tracks);
					sendSongPlayStats(currentTrackId, playLength, TRACK_END_REASON.NATURAL_COMPLETION);
				}

				// Reset the skipTrack flag after handling the skip
				setSkipTrack(false);

				// Start tracking the new track
				setCurrentTrackIndex(currentIndex);
				setCurrentTrackId(tracks[currentIndex].id);
			}

			if (!currentTrackId) {
				setCurrentTrackId(tracks[currentIndex].id);
			}

			setCurrentTrack(tracks[currentIndex].title);
			setCurrentArtist(tracks[currentIndex].artist);
		}
	}, [state?.played, state?.mixTracksSeconds, state.workout, state.workout?.mix?.tracks]);

	useEffect(() => {
		const nextMix = getWorkoutNextMixResponse?.data?.mix;

		if (nextMix) {
			setNextMix(nextMix);
		}
	}, [getWorkoutNextMixResponse]);

	useEffect(() => {
		if (playedInPercents >= 85 && !nextMix) {
			getWorkoutNextMixTrigger(workoutID);
		}
	}, [getWorkoutNextMixTrigger, nextMix, playedInPercents, workoutID]);

	const sendSongPlayStats = (trackId, playLength, endReason) => {
		if (
			!state.workout?.mix ||
			!trackId ||
			!domainGymData?.id ||
			!endReason ||
			(playLength < MIN_REPORT_PLAY_LENGTH && endReason !== TRACK_END_REASON.ERROR)
		) {
			return;
		}

		const payload = {
			mix_id: state.workout.mix.id,
			track_id: trackId,
			workout_id: workoutID,
			gym_id: domainGymData?.id,
			isrc: state.workout.mix.tracks.find((t) => t.id === trackId)?.isrc || "",
			source_detail: state.workout.mix.title,
			source_stream: state.workout.mix.url,
			end_reason: endReason,
			play_length: playLength,
		};

		try {
			sendSongPlayStatsTrigger(payload);
		} catch (error) {
			console.error("Failed to send song play stats:", error);
		}
	};

	const getPlayLength = (trackIndex, tracks) => {
		// Get the start time of the current track
		const currentTrackStartTime = tracks[trackIndex].time.split(":").reduce((acc, time) => acc * 60 + +time, 0);

		// Get the start time of the next track (or total mix duration if it's the last track)
		const nextTrackStartTime =
			trackIndex + 1 < tracks.length
				? tracks[trackIndex + 1].time.split(":").reduce((acc, time) => acc * 60 + +time, 0)
				: state.totalSeconds;

		// Calculate the play length of the current track
		const playedLength = Math.min(state.played, nextTrackStartTime) - currentTrackStartTime;
		const playedLengthSafe = Math.max(0, playedLength) || 0;

		return Math.ceil(playedLengthSafe);
	};

	const handleToggleMusic = () => {
		setPlayingMusic(!playingMusic);
	};

	const handleNativePlay = () => {
		if (!state.playing) {
			setState((prevState) => ({
				...prevState,
				playing: !state.playing,
				showing: state.playing,
			}));
		}
	};

	const handleNativePause = () => {
		if (state.playing) {
			setState((prevState) => ({
				...prevState,
				playing: !state.playing,
				showing: state.playing,
			}));
		}
	};

	const handleVolumeChange = (value, type) => {
		setState((prevState) => ({
			...prevState,
			[type === "music" ? "musicVolume" : "coachVolume"]: value / 100,
		}));
	};

	const handleSeekMouseUp = async (value) => {
		if (value === 100) {
			await handleMusicEnded();
		} else {
			const seconds = state.totalSeconds * (value / 100);
			setState((prevState) => ({ ...prevState, played: seconds }));
			musicRef.current.seekTo(value / 100);
		}
	};

	const handleTrackSkip = async () => {
		if (currentTrackId) {
			const playLength = getPlayLength(currentTrackIndex, state.workout.mix.tracks);
			sendSongPlayStats(currentTrackId, playLength, TRACK_END_REASON.USER_SKIPPED);
		}

		// Set the skipTrack flag to prevent natural completion stats
		setSkipTrack(true);

		let nextValue = -1;

		if (state.played >= state.mixTracksSeconds[state.mixTracksSeconds.length - 1]) {
			await handleMusicEnded();
			return;
		}

		for (let i = 0; i < state.mixTracksSeconds.length; i++) {
			if (state.played < state.mixTracksSeconds[i]) {
				nextValue = state.mixTracksSeconds[i];
				break;
			}
		}

		if (nextValue !== -1) {
			setState((prevState) => ({ ...prevState, played: nextValue }));
			musicRef.current.seekTo(nextValue);
		}
	};

	const handleProgress = ({ played }) => {
		const seconds = state.totalSeconds * played;
		setState((prevState) => ({ ...prevState, played: seconds }));
	};

	const handleMusicEnded = async () => {
		if (currentTrackId) {
			const playLength = getPlayLength(currentTrackIndex, state.workout.mix.tracks);
			sendSongPlayStats(currentTrackId, playLength, TRACK_END_REASON.NATURAL_COMPLETION);
		}

		let mix = nextMix;

		if (!nextMix) {
			const result = await getWorkoutNextMixTrigger(workoutID).unwrap();
			mix = result?.mix || state?.mix;
		}

		const lengthSplit = mix?.length?.split(":");
		const totalSeconds =
			lengthSplit && lengthSplit.length >= 3 ? +lengthSplit[0] * 60 * 60 + +lengthSplit[1] * 60 + +lengthSplit[2] : 0;
		const mixTracksSeconds = mix?.tracks?.map((track) => {
			const a = track.time.split(":");
			const currentValue = +a[0] * 60 * 60 + +a[1] * 60 + +a[2];
			return currentValue;
		});
		const workout = state.workout;
		workout.mix = mix;

		setState((prevState) => ({
			...prevState,
			workout,
			audioUrl: mix?.url,
			mix: mix,
			mixTracksSeconds,
			totalSeconds,
			played: 0,
		}));

		setNextMix(null);
		setCurrentTrackIndex(0);
		setCurrentTrackId(mix?.tracks?.[0]?.id);
	};

	const handleMusicError = (error) => {
		console.error("Music playback error:", error);

		if (currentTrackId) {
			const playLength = getPlayLength(currentTrackIndex, state.workout.mix.tracks);
			sendSongPlayStats(currentTrackId, playLength, TRACK_END_REASON.ERROR);
		}
	};

	const handleEnded = () => {
		setState((prevState) => ({
			...prevState,
			playing: false,
			seeking: false,
		}));
	};

	const renderTags = (tags) => {
		if (tags) {
			return tags.map((tag, index) => {
				const renderComma = index !== tags.length - 1;

				return (
					<span key={`tag-key-${index}`}>
						{tag}
						{renderComma ? "," : ""}
					</span>
				);
			});
		}
	};

	const onStart = () => {
		setPlayingMusic(true);
		const internalPlayer = videoRef.current.getInternalPlayer();
		internalPlayerRef.current = internalPlayer;

		if (!isUserCreator(userType)) {
			const initTime = mux.utils.now();
			mux.monitor(internalPlayerRef.current, {
				debug: false,
				data: {
					env_key: process.env.REACT_APP_MUX_ENV_KEY, // required
					player_name: "Web Player",
					player_init_time: initTime,
					viewer_user_id: `${state?.workout?.user_id || "Not Found"}`,
					experiment_name: "Dev Env Player",
					gym_id: `${state?.workout?.gym_id || "Not Found"}`,
					workout_id: `${state?.workout?.id || "Not Found"}`,
					sub_property_id: `${state?.workout?.id || "Not Found"}`,
					video_id: `${state?.workout?.id || "No ID Found"}`,
					video_title: `${state?.workout?.name || "No Title Found"}`,
					video_series: "Series",
					video_duration: `${state?.workout?.duration || "No Duration Found"}`,
					video_stream_type: "On Demand", // 'live' or 'on-demand'
				},
			});
		}
	};

	useEffect(() => {
		const player = livePlayerRef.current;
		if (player) {
			let lastTime = timeToBegin || 0; // Initialize lastTime with timeToBegin or 0
			player.addEventListener("timeupdate", () => {
				lastTime = player.currentTime;
			});

			const seeker = (e) => {
				e.preventDefault();
				if (Math.abs(player.currentTime - lastTime) > 1) {
					player.currentTime = lastTime; // snap back, preventing seek
				}
			};

			player.addEventListener("seeking", seeker);
			return () => {
				player.removeEventListener("seeking", seeker);
			};
		}
	}, [timeToBegin]);

	const preRecordedProps =
		isScheduledPreRecorded && isBeforeEndTime
			? {
					currentTime: timeToBegin || 0,
					autoPlay: true,
					controls: false,
					backwardSeekOffset: 0,
					forwardSeekOffset: 0,
			  }
			: {};
	if (paymentRequired) {
		return (
			<div>
				<MemberSubscriptionModal
					open={paymentRequired}
					onClose={() => navigate(`/${gym}/dashboard`)}
					afterSubscribe={() => window.location.reload()}
					gymId={domainGymData?.id}
					customerId={user?.customer_id}
					gymName={domainGymData?.name}
				/>
			</div>
		);
	}

	if (!state.videoUrl) {
		return <LoadingMask />;
	}

	if (livePlayerRef.current) {
		livePlayerRef.current.volume = state.coachVolume;
	}

	return (
		<Layout
			isUserCreator={isUserCreator(userType)}
			Content={
				<div className="video-player">
					<div className="workout-name">
						<Link
							style={{ color: "#FFF", marginRight: 16 }}
							to={isUserCreator(userType) ? "../consumer" : ".."}
							onClick={() => {
								const tracks = state.workout?.mix?.tracks;

								if (currentTrackId && tracks) {
									const playLength = getPlayLength(currentTrackIndex, tracks, state.played, state.totalSeconds);
									sendSongPlayStats(currentTrackId, playLength, TRACK_END_REASON.DEFAULT_OTHER);
								}
							}}
						>
							<ArrowLeftOutlined />
						</Link>
						{decodeString(state?.workout?.name)}
					</div>
					<div style={{ flex: 1 }}>
						<ReactPlayer
							style={{ display: "none" }}
							ref={musicRef}
							playing={playingMusic}
							volume={state.musicVolume}
							url={state.audioUrl}
							onStart={onStart}
							onProgress={handleProgress}
							onEnded={handleMusicEnded}
							onError={handleMusicError}
						/>

						{live && streamPlaybackId && playbackToken && thumbnailToken && (
							<MuxPlayer
								autoPlay
								controls
								ref={livePlayerRef}
								streamType="on-demand"
								playbackId={streamPlaybackId}
								metadata={
									!isUserCreator(userType)
										? {
												video_id: streamPlaybackId,
												video_title: state?.workout.name || "No Title Found",
												viewer_user_id: user?.id || "Unknown User",
										  }
										: {}
								}
								tokens={{
									playback: playbackToken,
									thumbnail: thumbnailToken,
								}}
								{...preRecordedProps}
							/>
						)}
						{!live && (
							<ReactPlayer
								playing={state.playing}
								volume={state.coachVolume}
								url={state.videoUrl}
								ref={videoRef}
								controls={true}
								onStart={onStart}
								onPlay={handleNativePlay}
								onPause={handleNativePause}
								onEnded={handleEnded}
								height={"calc(100vh -165px)"}
								width={"100%"}
							/>
						)}
					</div>
				</div>
			}
			Sider={
				<div>
					<div ref={paneControlRef} className="music-right-pane-controls">
						<div className="music-right-pane-title">
							<div
								className="image-container"
								style={{
									backgroundImage: `url(${state?.workout?.mix?.image_large})`,
								}}
							>
								<div
									className="image-container__inner"
									style={{
										backgroundImage: `url(${state?.workout?.mix?.dj?.image_large})`,
									}}
								/>
								<div className="image-container__text" dangerouslySetInnerHTML={{ __html: state?.mix?.dj?.name }} />
							</div>
							<h1 dangerouslySetInnerHTML={{ __html: state?.mix?.title }} />
							<div className="tags">{renderTags(state?.mix?.tags)}</div>
						</div>
						<Divider />
						<div className="music-right-pane-title">
							<h1 dangerouslySetInnerHTML={{ __html: currentTrack }} />
							<p dangerouslySetInnerHTML={{ __html: currentArtist }} />
						</div>
						<div className="music-controls">
							<div className="home-button" onClick={() => navigate("/dashboard")}>
								<img src={homeIcon} alt="home" />
							</div>
							<div className="play-button">
								<div onClick={handleToggleMusic}>
									{playingMusic ? (
										<img src={PauseSVG} alt="pause" />
									) : (
										<img src={PlaySVG} style={{ filter: "invert(100%)" }} alt="play" />
									)}
								</div>
							</div>
							<div className="skip-button" onClick={() => handleTrackSkip()}>
								<img src={skipNextIcon} alt="skip" />
							</div>
						</div>
						<Slider className="music-seek" value={playedInPercents} onAfterChange={handleSeekMouseUp} />
						<Divider />
						<div>
							<Row>
								<Col span={8}>
									<h1 className="music-seek-title">
										<img src={CoachSVG} alt="coach" /> COACH
									</h1>
								</Col>
								<Col span={8} offset={8}>
									<h1 style={{ float: "right" }} className="music-seek-title">
										{Math.round(state.coachVolume * 100)}%
									</h1>
								</Col>
							</Row>
							<div className="music-seek-container">
								<img src={lowVolumeSVG} alt="low" />
								<Slider
									value={state.coachVolume * 100}
									className="music-seek volume-slider"
									onChange={(e) => handleVolumeChange(e, "coach")}
								/>
								<img src={highVolumeSVG} alt="high" />
							</div>
						</div>
						<Divider />
						<div>
							<Row>
								<Col span={8}>
									<h1 className="music-seek-title">
										<img src={MusicSVG} alt="music" /> MUSIC
									</h1>
								</Col>
								<Col span={8} offset={8}>
									<h1 style={{ float: "right" }} className="music-seek-title">
										{Math.round(state.musicVolume * 100)}%
									</h1>
								</Col>
							</Row>
							<div className="music-seek-container">
								<img src={lowVolumeSVG} alt="low" />
								<Slider
									value={state.musicVolume * 100}
									className="music-seek volume-slider"
									onChange={(e) => handleVolumeChange(e, "music")}
								/>
								<img src={highVolumeSVG} alt="high" />
							</div>
						</div>
					</div>
				</div>
			}
		/>
	);
}

export default FullScreenWorkoutVideoMusicPlayer;
